import type { ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";

import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { TableColumn } from "@/components/ui/TableV2";
import { OrganizationMembersMemberRow } from "@/types/organization-member";

export type BreakGlassOwnerRow = OrganizationMembersMemberRow;

interface UseBreakGlassOwnersColumnsParams {
  onAssign: (userId: number, ownerEmail: string) => void;
  isUpdating: boolean;
}

export const useBreakGlassOwnersColumns = ({
  onAssign,
  isUpdating,
}: UseBreakGlassOwnersColumnsParams) => {
  const columns = useMemo<TableColumn<BreakGlassOwnerRow>[]>(
    () => [
      {
        id: 1,
        name: "owner",
        header: "Owner",
        sortable: false,
        minWidth: 220,
        flex: 1,
        cellClass: "justify-start text-left",
        headerClass: "!justify-start !text-left",
        cellRenderer: (params: ICellRendererParams<BreakGlassOwnerRow>) => {
          const owner = params.data;
          if (!owner) {
            return "";
          }
          const displayName = owner.user.name || owner.user.email;
          return (
            <div className="flex h-full w-full items-center justify-start px-3 text-left">
              <div className="min-w-0">
                <OverflowDisplayTooltip content={displayName}>
                  <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
                </OverflowDisplayTooltip>
                <OverflowDisplayTooltip content={owner.user.email}>
                  <p className="truncate text-xs text-gray-500">{owner.user.email}</p>
                </OverflowDisplayTooltip>
              </div>
            </div>
          );
        },
      },
      {
        id: 2,
        name: "emergency_access",
        header: "Emergency access",
        sortable: false,
        minWidth: 160,
        maxWidth: 200,
        cellClass: "justify-start text-left",
        headerClass: "!justify-start !text-left",
        cellRenderer: (params: ICellRendererParams<BreakGlassOwnerRow>) => {
          const owner = params.data;
          if (!owner) {
            return "";
          }
          const inputId = `break-glass-owner-${owner.user_id}`;
          return (
            <div className="flex h-full w-full items-center justify-start gap-2 px-3 text-left">
              <input
                id={inputId}
                type="radio"
                name="break-glass-owner"
                checked={owner.is_break_glass}
                disabled={isUpdating}
                onChange={() => onAssign(owner.user_id, owner.user.email)}
                className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor={inputId} className="text-xs text-gray-600">
                {owner.is_break_glass ? "Assigned" : "Assign"}
              </label>
            </div>
          );
        },
      },
    ],
    [isUpdating, onAssign],
  );

  return { columns };
};
